import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format, isPast } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useMyBookings, type UserBooking } from '@/hooks/useMyBookings';
import { useMyAccommodationBookings, type UserAccommodationBooking } from '@/hooks/useMyAccommodationBookings';
import { getBookingListStatus } from '@/lib/booking/listStatus';
import { useMyOrders } from '@/hooks/useMyOrders';
import { groupBookings, type BookingGroup } from '@/lib/groupBookings';
import { MyOrdersSection } from '@/components/account/MyOrdersSection';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  ExternalLink,
  Loader2,
  Compass,
  Users,
  X,
} from 'lucide-react';
import { ActivitiesSection } from '@/features/account/ActivitiesSection';

function formatDateSv(dateStr: string) {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'd MMM yyyy, HH:mm', { locale: sv });
  } catch {
    return '—';
  }
}

function formatDateShort(dateStr: string) {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'd MMM yyyy', { locale: sv });
  } catch {
    return '—';
  }
}

export default function MyBookingsPage() {
  const { bookings, isLoading, error, cancelBooking, isCancelling } = useMyBookings();
  const { bookings: accBookings, isLoading: accLoading, error: accError } = useMyAccommodationBookings();
  const { data: orders } = useMyOrders();
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<UserBooking | null>(null);
  const { toast } = useToast();

  const hasBookings = bookings.length > 0;
  const hasAccBookings = accBookings.length > 0;
  const hasOrders = (orders ?? []).length > 0;
  const bothEmpty = !hasBookings && !hasAccBookings && !hasOrders;
  const anyLoading = isLoading || accLoading;

  const groups = useMemo(() => groupBookings(bookings), [bookings]);

  const upcomingGroups = useMemo(
    () =>
      groups
        .filter((g) => g.status !== 'cancelled' && !isPast(new Date(g.startAt)))
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
    [groups]
  );

  const pastGroups = useMemo(
    () =>
      groups
        .filter((g) => g.status === 'cancelled' || isPast(new Date(g.startAt)))
        .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime()),
    [groups]
  );

  const handleCancelClick = (booking: UserBooking) => {
    setSelectedBooking(booking);
    setConfirmDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;
    setCancellingBookingId(selectedBooking.id);
    setConfirmDialogOpen(false);
    try {
      await cancelBooking(selectedBooking.confirmation_token);
      toast({
        title: 'Bokningen är avbokad',
        description: `Din bokning till "${selectedBooking.event_title}" har avbokats.`,
      });
    } catch (err: any) {
      toast({
        title: 'Kunde inte avboka',
        description: err.message || 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    } finally {
      setCancellingBookingId(null);
      setSelectedBooking(null);
    }
  };

  const handleCancelAll = (group: BookingGroup) => {
    const firstConfirmed = group.items.find((b) => b.status === 'confirmed');
    if (firstConfirmed) handleCancelClick(firstConfirmed);
  };

  const lastBookedDate = useMemo(() => {
    if (bookings.length === 0) return null;
    const sorted = [...bookings].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0]?.created_at ?? null;
  }, [bookings]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pt-6 pb-24">
      {/* Back button */}
      <div className="flex items-center">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
          <Link to="/app/customer">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
      </div>

      <div className="mt-6">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
          Mina bokningar & beställningar
        </h1>

        {/* Loading */}
        {anyLoading && (
          <div className="mt-6 space-y-4 max-w-xl">
            {[1, 2].map((i) => (
              <Card key={i} className="border-border/60 border-dashed shadow-none">
                <CardContent className="flex items-center gap-4 p-5 sm:p-6">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error */}
        {(error || accError) && !anyLoading && (
          <Card className="mt-6 border-destructive/50 bg-destructive/5 shadow-none max-w-xl">
            <CardContent className="p-5 sm:p-6 text-center text-destructive">
              <p>Kunde inte hämta bokningar. Försök igen senare.</p>
            </CardContent>
          </Card>
        )}

        {/* Combined empty state: both bookings AND orders empty */}
        {!anyLoading && !error && !accError && bothEmpty && (
          <div className="mt-8 space-y-12">
            {/* Empty state card */}
            <Card className="border-border/60 border-dashed shadow-none max-w-xl">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-5">
                  <Compass className="h-7 w-7 text-muted-foreground" />
                </div>
                <p className="text-base font-medium text-foreground mb-1.5">
                  Här samlas dina bokningar och beställningar
                </p>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed max-w-sm">
                  När du bokar en aktivitet eller planerar en vistelse dyker den upp här.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild>
                    <Link to="/explore?tab=activities">
                      Utforska aktiviteter
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/explore?tab=places">
                      Upptäck boenden
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Discovery section */}
            <ActivitiesSection />
          </div>
        )}

        {/* Content: at least one of bookings or orders has data */}
        {!anyLoading && !error && !accError && !bothEmpty && (
          <>
            {/* Desktop: 2-col grid */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-8 mt-8">
              {/* Left: booking + order lists */}
              <div className="md:col-span-8 space-y-10">
                {/* Aktiviteter — only if bookings exist */}
                {hasBookings && (
                  <section data-testid="bookings-section">
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                      Aktiviteter
                    </h2>

                    {/* Upcoming */}
                    {upcomingGroups.length > 0 && (
                      <div className="space-y-3 mb-6">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Kommande</p>
                        {upcomingGroups.map((group) => (
                          <BookingGroupCard
                            key={group.key}
                            group={group}
                            cancellingBookingId={cancellingBookingId}
                            onCancelClick={handleCancelClick}
                            onCancelAll={handleCancelAll}
                          />
                        ))}
                      </div>
                    )}

                    {upcomingGroups.length === 0 && (
                      <p className="text-sm text-muted-foreground/60 py-4">
                        Inga kommande bokningar.
                      </p>
                    )}

                    {/* Past */}
                    {pastGroups.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Tidigare</p>
                        {pastGroups.map((group) => (
                          <BookingGroupCard
                            key={group.key}
                            group={group}
                            cancellingBookingId={null}
                            onCancelClick={() => {}}
                            onCancelAll={() => {}}
                            isPastOrCancelled
                          />
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* Boende — only if accommodation bookings exist */}
                {hasAccBookings && (
                  <section data-testid="accommodation-bookings-section">
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
                      Boende
                    </h2>
                    <div className="space-y-3">
                      {accBookings.map((b) => (
                        <AccommodationBookingCard key={b.id} booking={b} />
                      ))}
                    </div>
                  </section>
                )}

                {hasOrders && (
                  <section data-testid="orders-section">
                    <MyOrdersSection hideWhenEmpty />
                  </section>
                )}
              </div>

              {/* Right: summary panel */}
              <div className="md:col-span-4">
                <div data-testid="bookings-summary" className="sticky top-8">
                  <Card className="border-border/50 shadow-none">
                    <CardContent className="p-6 space-y-5">
                      <h3 className="text-sm font-medium text-foreground">Översikt</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Kommande bokningar</span>
                          <span className="font-medium text-foreground">{upcomingGroups.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Tidigare bokningar</span>
                          <span className="font-medium text-foreground">{pastGroups.length}</span>
                        </div>
                        {lastBookedDate && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Senast bokad</span>
                            <span className="font-medium text-foreground">
                              {formatDateShort(lastBookedDate)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="pt-2 border-t border-border/30">
                        <Button asChild variant="outline" size="sm" className="w-full">
                          <Link to="/">
                            Utforska aktiviteter
                            <ExternalLink className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Mobile: stacked CardList */}
            <div className="md:hidden mt-6 space-y-8">
              {/* Aktiviteter — only if bookings exist */}
              {hasBookings && (
                <section>
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Aktiviteter
                  </h2>
                  {upcomingGroups.length > 0 && (
                    <div className="space-y-3">
                      {upcomingGroups.map((group) => (
                        <BookingGroupCard
                          key={group.key}
                          group={group}
                          cancellingBookingId={cancellingBookingId}
                          onCancelClick={handleCancelClick}
                          onCancelAll={handleCancelAll}
                        />
                      ))}
                    </div>
                  )}

                  {pastGroups.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {upcomingGroups.length > 0 && (
                        <p className="text-sm text-muted-foreground">Tidigare / avbokade</p>
                      )}
                      {pastGroups.map((group) => (
                        <BookingGroupCard
                          key={group.key}
                          group={group}
                          cancellingBookingId={null}
                          onCancelClick={() => {}}
                          onCancelAll={() => {}}
                          isPastOrCancelled
                        />
                      ))}
                    </div>
                  )}

                  {upcomingGroups.length === 0 && pastGroups.length === 0 && (
                    <p className="text-sm text-muted-foreground/60">Inga bokningar.</p>
                  )}
                </section>
              )}

              {/* Boende — mobile */}
              {hasAccBookings && (
                <section>
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Boende
                  </h2>
                  <div className="space-y-3">
                    {accBookings.map((b) => (
                      <AccommodationBookingCard key={b.id} booking={b} />
                    ))}
                  </div>
                </section>
              )}

              {/* Beställningar — only if orders exist */}
              {hasOrders && (
                <section>
                  <MyOrdersSection hideWhenEmpty />
                </section>
              )}
            </div>
          </>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent data-testid="booking-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Avboka bokning</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill avboka din bokning till{' '}
              <strong>{selectedBooking?.event_title}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              data-testid="booking-cancel-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Avboka
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ── Accommodation Booking Card ── */

function AccommodationBookingCard({ booking }: { booking: UserAccommodationBooking }) {
  const isPast = new Date(booking.check_out) < new Date();
  const isCancelled = booking.status === 'cancelled';
  const faded = isPast || isCancelled;

   const hasPayment = booking.amount_paid_ore > 0;

  // Use centralized status mapping — same logic as producer view
  const listStatus = getBookingListStatus({
    type: 'accommodation',
    status: booking.status,
    paymentStatus: booking.payment_status,
    amountPaidOre: booking.amount_paid_ore,
    totalStayOre: null, // not available at list level
    requiresPrepayment: false,
    prepaymentAmountOre: null,
  });

  const statusLabel = isCancelled
    ? 'Avbokad'
    : isPast
      ? 'Genomförd'
      : listStatus.label;

  const statusVariant = isCancelled ? 'destructive' as const : 'default' as const;

  return (
    <Link to={`/app/customer/bookings/${booking.id}`} className="block group">
      <Card className={faded ? 'opacity-60 shadow-none' : 'border-border/60 shadow-none transition-colors group-hover:border-border'}>
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0 mt-0.5">
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium line-clamp-2">{booking.accommodation_title}</p>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap mt-2">
                <span>{formatDateShort(booking.check_in)} – {formatDateShort(booking.check_out)}</span>
                <span className="hidden sm:inline">·</span>
                <span>{booking.producer_name}</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {booking.guests} {booking.guests === 1 ? 'gäst' : 'gäster'}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/* ── Booking Group Card ── */

interface BookingGroupCardProps {
  group: BookingGroup;
  cancellingBookingId: string | null;
  onCancelClick: (booking: UserBooking) => void;
  onCancelAll: (group: BookingGroup) => void;
  isPastOrCancelled?: boolean;
}

function BookingGroupCard({
  group,
  cancellingBookingId,
  onCancelClick,
  onCancelAll,
  isPastOrCancelled,
}: BookingGroupCardProps) {
  const isSingle = group.items.length === 1;
  const statusVariant = group.status === 'cancelled' ? 'destructive' : 'default';
  const statusLabel = group.status === 'cancelled' ? 'Avbokad' : 'Bekräftad';
  const hasConfirmed = group.items.some((b) => b.status === 'confirmed');
  const dateStr = formatDateSv(group.startAt);

  if (isSingle) {
    const booking = group.items[0];
    return (
      <Card
        data-testid="booking-card"
        className={isPastOrCancelled ? 'opacity-60 shadow-none' : 'border-border/60 shadow-none'}
      >
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0 mt-0.5">
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium line-clamp-2">{group.title}</p>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap mt-2">
                <span>{dateStr}</span>
                <span className="hidden sm:inline">·</span>
                <span>{group.producerName}</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {booking.spots} {booking.spots === 1 ? 'plats' : 'platser'}
                </span>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2 mt-4">
                <Button
                  data-testid="booking-open-activity"
                  asChild
                  variant="outline"
                  size="sm"
                >
                  <Link to={`/event/${group.eventId}`}>Visa aktivitet</Link>
                </Button>
                {booking.status === 'confirmed' && !isPastOrCancelled && (
                  <Button
                    data-testid="booking-cancel"
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancelClick(booking)}
                    disabled={cancellingBookingId === booking.id}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    {cancellingBookingId === booking.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-1" />
                        Avboka
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Multi-booking group: collapsible
  return (
    <Collapsible>
      <Card
        data-testid="booking-card"
        className={isPastOrCancelled ? 'opacity-60 shadow-none' : 'border-border/60 shadow-none'}
      >
        <CardContent className="p-5 sm:p-6 space-y-0">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0 mt-0.5">
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium line-clamp-2">{group.title}</p>
                <Badge variant={statusVariant}>{statusLabel}</Badge>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap mt-2">
                <span>{dateStr}</span>
                <span className="hidden sm:inline">·</span>
                <span>{group.producerName}</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {group.totalSpots} platser totalt
                </span>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2 mt-4">
                <Button
                  data-testid="booking-open-activity"
                  asChild
                  variant="outline"
                  size="sm"
                >
                  <Link to={`/event/${group.eventId}`}>Visa aktivitet</Link>
                </Button>
                {hasConfirmed && !isPastOrCancelled && (
                  <Button
                    data-testid="booking-cancel"
                    variant="ghost"
                    size="sm"
                    onClick={() => onCancelAll(group)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Avboka alla
                  </Button>
                )}
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-muted-foreground">
                    <span className="mr-1">Visa ({group.items.length})</span>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          </div>

          <CollapsibleContent>
            <div className="mt-3 border-t pt-3 space-y-2">
              {group.items.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between pl-14 py-1.5 text-sm"
                >
                  <div className="flex items-center gap-3 text-muted-foreground flex-wrap min-w-0">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {booking.spots} {booking.spots === 1 ? 'plats' : 'platser'}
                    </span>
                    <span className="text-xs">
                      Bokad {formatDateShort(booking.created_at)}
                    </span>
                  </div>
                  {booking.status === 'confirmed' && !isPastOrCancelled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCancelClick(booking)}
                      disabled={cancellingBookingId === booking.id}
                      className="text-destructive hover:bg-destructive/10 h-7 px-2 text-xs"
                    >
                      {cancellingBookingId === booking.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        'Avboka'
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
}
